import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'

import { setResolvedEnvironment } from '../config/resolved-environment'

/**
 * The slice of `runtimeConfig.public` this plugin needs.
 *
 * `appEnv` is set in `libs/config/nuxt.base.config.ts` and is the same source `useEnv()` reads.
 */
export interface ResolveEnvironmentRuntimeConfig {
  appEnv?: string
}

export interface ResolveEnvironmentFailClosedContext {
  /** Vite/Nuxt production client/server build (`import.meta.env.PROD`). */
  isProdBuild?: boolean
  /** Build/runtime `NUXT_ENV` — authoritative for membership apps. */
  nuxtEnv?: string | undefined
  /** Node process env — production when serving built output. */
  nodeEnv?: string | undefined
}

/**
 * True when a failed or missing runtime environment must not fall open to `development`.
 *
 * Exported for unit tests so production fail-closed behavior can be asserted without a Nuxt app.
 */
export function expectsProductionRuntimeConfig(context: ResolveEnvironmentFailClosedContext = {}): boolean {
  const isProdBuild = context.isProdBuild ?? import.meta.env.PROD
  const nuxtEnv = context.nuxtEnv ?? (typeof process !== 'undefined' ? process.env?.NUXT_ENV : undefined)
  const nodeEnv = context.nodeEnv ?? (typeof process !== 'undefined' ? process.env?.NODE_ENV : undefined)

  return Boolean(isProdBuild || nuxtEnv === 'production' || nodeEnv === 'production')
}

/**
 * Publish the environment for `config/index.ts` to read.
 *
 * Exported separately from the plugin so it is testable without a Nuxt app context — the same
 * pattern as `runInitApp` and `runInitLocation`.
 *
 * In production contexts, `appEnv` is required so callers cannot silently select development
 * endpoints/analytics after a missing runtime config value.
 */
export function runResolveEnvironment(
  publicConfig: ResolveEnvironmentRuntimeConfig,
  context: ResolveEnvironmentFailClosedContext = {}
): void {
  if (expectsProductionRuntimeConfig(context) && !publicConfig.appEnv) {
    throw new Error('[Resolve-Environment] Missing runtime public.appEnv in production')
  }

  setResolvedEnvironment(publicConfig.appEnv)
}

/**
 * Handle `useRuntimeConfig()` failure from the pre plugin.
 *
 * Production must fail closed: falling through leaves `getResolvedEnvironment()` unset after
 * `window.__NUXT__` is removed, so `getConfig()` selects development API/DRM/analytics values.
 */
export function handleResolveEnvironmentFailure(error: unknown, context: ResolveEnvironmentFailClosedContext = {}): void {
  if (expectsProductionRuntimeConfig(context)) {
    throw error instanceof Error ? error : new Error(`[Resolve-Environment] Failed to read runtime config in production: ${String(error)}`)
  }

  // Non-production: nothing else can supply the environment this early, so resolution will fall
  // back to `development`. Warn loudly rather than let that pass unnoticed.
  console.warn('[Resolve-Environment] Failed to read runtime config; environment will fall back to development:', error)
}

/**
 * Publishes the environment ahead of every default-order plugin.
 *
 * Nuxt's `nuxt:revive-payload:client` runs at order -30 and deletes `window.__NUXT__`. From that
 * point until the environment is published, every source `getEnvironment()` consults is empty:
 * the override is unset, the global is gone, and `process.env.NUXT_ENV` is `undefined` on the
 * client because Nuxt's client shim is `{}`. `getConfig()` therefore resolves `development`.
 *
 * `media-layer/plugins/init-app.client.ts` and `analytics-layer/plugins/analytics.client.ts` both
 * call `getConfig()` at default order, so before this plugin existed a production client
 * initialised the Assets DRM client against staging and reported to the development GA stream.
 * Neither failure throws, so a Sentry-based soak could not detect it.
 *
 * `enforce: 'pre'` is safe here: `useRuntimeConfig()` reads `nuxtApp.payload.config`, which
 * `createNuxtApp` populates before any plugin runs, so this does not depend on
 * `nuxt:revive-payload:client`. It deliberately does NOT touch pinia — `setAppType` stays in
 * `init-app` at default order, because `pre` runs ahead of `@pinia/nuxt`'s own order-0 plugin and
 * `setAppType` needs an active pinia.
 *
 * Do NOT rename this file with a numeric ordering prefix. Ordering comes from `enforce: 'pre'`,
 * not the filename, and a `00.` prefix reproducibly breaks the clientportal production build with
 * `RangeError: [commonjs--resolver] Maximum call stack size exceeded` in
 * `@ghl-revex/assets-drm-client` — sorting this plugin first perturbs the module graph enough to
 * trip rollup's CommonJS resolver. Verified by bisect: identical file contents build at exit 0
 * without the prefix and fail at exit 1 with it.
 */
export default defineNuxtPlugin({
  name: 'core:resolve-environment',
  enforce: 'pre',
  setup() {
    try {
      // Cast the config itself rather than its `public` property: this repo's `RuntimeConfig`
      // type does not declare `public`, so reaching through it is a type error (TS2339).
      const { public: publicConfig } = useRuntimeConfig() as unknown as { public: ResolveEnvironmentRuntimeConfig }
      runResolveEnvironment(publicConfig)
    } catch (error) {
      handleResolveEnvironmentFailure(error)
    }
  },
})
